import { Fragment } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { ModalProvider } from './contexts/ModalContext'
import { AuthProvider } from './contexts/AuthContext'
import { CampaignProvider } from './contexts/CampaignContext'
import LoginPage from './pages/LoginPage'
import CampaignPickerPage from './pages/CampaignPickerPage'
import CampaignPage from './pages/CampaignPage'
import SettingsPage from './pages/SettingsPage'
import InteractiveMapPage from './pages/InteractiveMapPage'
import FeywildPage from './pages/FeywildPage'
import CampaignSettingsPage from './pages/CampaignSettingsPage'
import ProtectedRoute from './components/ProtectedRoute'
import { RouteResetErrorBoundary } from './components/ErrorBoundary'
import VersionBadge from './components/VersionBadge'
import UpdatePrompt from './components/UpdatePrompt'

// React Router does not remount a component when only a route param changes
// (e.g. switching campaigns via the TopBar, /c/sword-coast/campaign ->
// /c/other-campaign/campaign). Without this, CampaignPage and every tab keep
// whatever data they fetched for the previous campaign, since their effects
// have [] dependency arrays. Keying on the slug forces the whole subtree to
// remount on a switch. It sits BELOW CampaignProvider so switching doesn't
// also re-fetch /api/campaigns.
function CampaignKeyed({ children }) {
  const { slug } = useParams()
  return <Fragment key={slug}>{children}</Fragment>
}

// Everything under /c/:slug shares one CampaignProvider, which reads the slug
// from the route params and resolves it against GET /api/campaigns.
//
// CampaignProvider MUST stay nested inside ProtectedRoute: its loadCampaigns
// returns early when there is no user and never clears `loading` on that
// path, so mounting it without an authenticated user wedges the UI forever.
function CampaignScope({ children }) {
  return (
    <ProtectedRoute>
      <CampaignProvider>
        <CampaignKeyed>{children}</CampaignKeyed>
      </CampaignProvider>
    </ProtectedRoute>
  )
}

function App() {
  return (
    <AuthProvider>
      <ModalProvider>
        {/* Rendered inside the providers but outside <Routes> (and outside
            RouteResetErrorBoundary below) so the build identity stays on
            screen on every page, including /login, and even if a route
            crashes — that's exactly when knowing the build matters most. */}
        <VersionBadge />
        <UpdatePrompt />
        <Router>
          {/* The boundary MUST live inside Router, wrapping Routes — not
              outside/above AuthProvider or ModalProvider. AuthProvider is
              what renders SessionExpiredModal (see AuthContext.jsx); if a
              page-level render crash unmounted AuthProvider along with
              everything else, it would also destroy the re-auth modal that
              exists specifically to recover from an expired/invalid
              session — which is the exact incident this boundary is meant
              to fix, not reintroduce one level up. Keeping the boundary
              below both providers means a crash anywhere in a route's
              subtree is caught locally: the fallback UI renders in place of
              the broken page, while AuthProvider/ModalProvider (and the
              session-expired modal) stay mounted and fully functional.
              RouteResetErrorBoundary needs useLocation(), which requires a
              Router ancestor, so it can't be hoisted any higher anyway. */}
          <RouteResetErrorBoundary>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                path="/campaigns"
                element={<ProtectedRoute><CampaignPickerPage /></ProtectedRoute>}
              />

              <Route path="/c/:slug/campaign" element={<CampaignScope><CampaignPage /></CampaignScope>} />
              <Route path="/c/:slug/map" element={<CampaignScope><InteractiveMapPage /></CampaignScope>} />
              <Route path="/c/:slug/feyblood" element={<CampaignScope><FeywildPage /></CampaignScope>} />
              <Route path="/c/:slug/settings" element={<CampaignScope><CampaignSettingsPage /></CampaignScope>} />

              {/* Account + site admin. Deliberately outside campaign scope. */}
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

              {/* Old single-campaign URLs. Preserved so existing bookmarks land somewhere sane. */}
              <Route path="/campaign" element={<Navigate to="/campaigns" replace />} />
              <Route path="/campaign/map" element={<Navigate to="/campaigns" replace />} />
              <Route path="/campaign/feyblood" element={<Navigate to="/campaigns" replace />} />

              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/campaigns" replace />} />
            </Routes>
          </RouteResetErrorBoundary>
        </Router>
      </ModalProvider>
    </AuthProvider>
  )
}

export default App
